import React, { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  Layers, ArrowRight, CheckCircle2, User, Mail, Phone, MapPin, Sparkles,
  ChevronRight, ChevronLeft, IndianRupee, Ruler, Landmark, Compass
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import PropertyCard from "@/components/PropertyCard";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const CATEGORIES = ["Plot / Land"];
const ROAD_WIDTH_OPTIONS = ["12 ft", "24 ft", "30 ft", "40 ft", "60 ft", "80 ft+"];
const FACING_OPTIONS = ["East", "West", "North", "South", "North-East", "North-West", "South-East", "South-West"];

const formatCurrency = (val) => {
  if (val >= 10000000) {
    return `₹${(val / 10000000).toFixed(2)} Cr`;
  }
  if (val >= 100000) {
    return `₹${(val / 100000).toFixed(2)} Lakh`;
  }
  return `₹${val.toLocaleString("en-IN")}`;
};

export default function PlotOnDemand() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { register, handleSubmit, setValue, watch, trigger, formState: { isSubmitting } } = useForm({
    defaultValues: {
      category: "Plot / Land",
      area_unit: "sqyd",
      purpose: "SELF_USE",
      timeframe: "IMMEDIATE"
    }
  });

  const [matches, setMatches] = useState(null);
  const [step, setStep] = useState(1);
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);
  const redirectTimerRef = useRef(null);
  const cat = watch("category");
  const areaUnit = watch("area_unit");

  // Slider States
  const [budgetRange, setBudgetRange] = useState([5000000, 50000000]); // Default 50L - 5Cr
  const [areaRange, setAreaRange] = useState([200, 2000]); // Default 200 - 2000 sqyd/sqft

  // Sync sliders to react-hook-form
  useEffect(() => {
    setValue("budget_min", budgetRange[0]);
    setValue("budget_max", budgetRange[1]);
  }, [budgetRange, setValue]);

  useEffect(() => {
    setValue("area_min", areaRange[0]);
    setValue("area_max", areaRange[1]);
  }, [areaRange, setValue]);

  // Automatically pre-fill contact details from logged-in user profile
  useEffect(() => {
    if (user) {
      setValue("name", user.name || "");
      setValue("email", user.email || "");
      setValue("phone", user.phone || user.contact_phone || "");
    }
  }, [user, setValue]);

  useEffect(() => () => {
    if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
  }, []);

  const nextStep = async () => {
    let fieldsToValidate = [];
    if (step === 1) {
      fieldsToValidate = ["name", "email", "phone"];
    } else if (step === 2) {
      fieldsToValidate = ["city", "locality", "category"];
    }

    const isValid = await trigger(fieldsToValidate);
    if (isValid) {
      setStep(prev => prev + 1);
    } else {
      toast.error("Please fill in all required fields before proceeding.");
    }
  };

  const prevStep = () => {
    setStep(prev => prev - 1);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && step < 3) {
      e.preventDefault();
      nextStep();
    }
  };

  const onSubmit = async (values) => {
    if (step !== 3) {
      return;
    }
    try {
      const payload = {
        ...values,
        budget_max: Number(values.budget_max),
        budget_min: values.budget_min ? Number(values.budget_min) : undefined,
        area_min: values.area_min ? Number(values.area_min) : undefined,
        area_max: values.area_max ? Number(values.area_max) : undefined,
      };
      const { data } = await api.post("/plot-on-demand", payload);
      setMatches(data);
      setShowSuccessOverlay(true);
      
      // Navigate to /dashboard/plot-demands after 2.5s success animation displays
      redirectTimerRef.current = setTimeout(() => {
        navigate("/dashboard/plot-demands");
      }, 2500);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Intercept premature submit events
  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (step === 3) {
      handleSubmit(onSubmit)();
    }
  };

  const handleSuccessOpenChange = (open) => {
    setShowSuccessOverlay(open);
    if (!open && redirectTimerRef.current) {
      clearTimeout(redirectTimerRef.current);
      redirectTimerRef.current = null;
    }
  };

  return (
    <div className="premium-page-surface bg-transparent min-h-screen flex flex-col" data-testid="plot-on-demand-page">
      
      {/* Main Split Section on Desktop, Stacked on Mobile */}
      <div className="flex-1 grid lg:grid-cols-12 min-h-[calc(100vh-80px)]">
        
        {/* Left Side: Marketing Info & Copy */}
        <div className="lg:col-span-5 relative overflow-hidden bg-slate-950 text-white flex flex-col justify-center p-8 sm:p-12 lg:p-16 border-r border-slate-900 shadow-2xl">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-900/30 via-slate-950 to-slate-950 pointer-events-none" />
          
          <div className="relative z-10 space-y-6 max-w-lg mx-auto lg:mx-0">
            <p className="gold-eyebrow text-xs font-bold uppercase tracking-[0.25em] mb-3 inline-flex items-center gap-1.5">
              <Layers className="w-3.5 h-3.5" /> Plots on Demand
            </p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05]">
              Can't find what you want? <br />
              <span className="bg-gradient-to-r from-blue-400 via-indigo-300 to-emerald-300 bg-clip-text text-transparent">Share your requirement.</span>
            </h1>
            <p className="text-slate-400 leading-relaxed text-sm sm:text-base">
              Tell us your requirements. Approved requests can be reviewed by participating offices and agents.
            </p>
            
            <ul className="space-y-3.5 pt-6 text-sm border-t border-slate-800/80">
              {[
                "Request review by participating agents in Noida and NCR",
                "Property suggestions based on your saved requirements",
                "Track request status and suggested matches in your dashboard",
                "Review all commercial terms before accepting a match"
              ].map((b, i) => (
                <li key={i} className="flex items-center gap-2.5 text-slate-300">
                  <CheckCircle2 className="w-4 h-4 text-[hsl(var(--success))] shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Right Side: Wizard Form Panel */}
        <div className="lg:col-span-7 flex flex-col justify-center p-6 sm:p-12 lg:p-16 bg-slate-50/30 dark:bg-slate-900/10">
          <div className="max-w-xl w-full mx-auto">
            <div className="bg-card border border-border shadow-xl rounded-3xl overflow-hidden p-6 sm:p-8 space-y-6">
              
              {/* Step Progress Tracker */}
              <div className="flex items-center justify-between border-b border-border/60 pb-5">
                {[
                  { number: 1, label: "Contact" },
                  { number: 2, label: "Requirements" },
                  { number: 3, label: "Size & Budget" }
                ].map((s) => (
                  <div key={s.number} className="flex items-center gap-2">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black transition-all ${
                      step === s.number
                        ? "bg-primary text-white scale-110 shadow-sm"
                        : step > s.number
                          ? "bg-emerald-500 text-white"
                          : "bg-secondary text-muted-foreground"
                    }`}>
                      {step > s.number ? "✓" : s.number}
                    </div>
                    <span className={`text-xs font-bold ${
                      step === s.number ? "text-primary" : "text-muted-foreground"
                    } hidden sm:inline`}>
                      {s.label}
                    </span>
                    {s.number < 3 && <ChevronRight className="w-4 h-4 text-muted-foreground/40 hidden sm:block" />}
                  </div>
                ))}
              </div>

              {/* Stepper Form */}
              <form onSubmit={handleFormSubmit} onKeyDown={handleKeyDown} className="space-y-6" data-testid="plot-form">
                <AnimatePresence mode="wait">
                  
                  {/* STEP 1: Contact Details */}
                  {step === 1 && (
                    <motion.div
                      key="step-1"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.2 }}
                      className="space-y-4"
                    >
                      <div>
                        <h2 className="text-xl font-bold font-display text-primary">Contact Details</h2>
                        <p className="text-xs text-muted-foreground">Verify your information so matches can reach you.</p>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="font-bold flex items-center gap-1.5"><User className="w-3.5 h-3.5 text-muted-foreground" /> Your Name</Label>
                          <Input {...register("name", { required: true })} placeholder="e.g. Amit Yadav" data-testid="plot-name" className="rounded-xl border-border" />
                        </div>
                        <div className="space-y-2">
                          <Label className="font-bold flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-muted-foreground" /> Email Address</Label>
                          <Input type="email" {...register("email", { required: true })} placeholder="e.g. amit@example.com" data-testid="plot-email" className="rounded-xl border-border" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="font-bold flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-muted-foreground" /> Contact Phone</Label>
                        <Input {...register("phone", { required: true })} placeholder="e.g. +919999999999" data-testid="plot-phone" className="rounded-xl border-border" />
                      </div>
                      
                      {user && (
                        <div className="p-3.5 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-xs font-medium flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-500" />
                          <span>Pre-filled details from your signed-in ShreeHomes buyer account.</span>
                        </div>
                      )}
                    </motion.div>
                  )}

                  {/* STEP 2: Location & Type */}
                  {step === 2 && (
                    <motion.div
                      key="step-2"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.2 }}
                      className="space-y-4"
                    >
                      <div>
                        <h2 className="text-xl font-bold font-display text-primary">Sourcing Criteria</h2>
                        <p className="text-xs text-muted-foreground">Select where and what you are looking for.</p>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="font-bold flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-muted-foreground" /> Target City</Label>
                          <Input {...register("city", { required: true })} placeholder="e.g. Noida" data-testid="plot-city" className="rounded-xl border-border" />
                        </div>
                        <div className="space-y-2">
                          <Label className="font-bold flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-muted-foreground" /> Locality / Sector</Label>
                          <Input {...register("locality", { required: true })} placeholder="e.g. Sector 62" data-testid="plot-locality" className="rounded-xl border-border" />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="font-bold">Property Category</Label>
                        <Select value={cat} onValueChange={(v) => setValue("category", v)}>
                          <SelectTrigger data-testid="plot-category" className="rounded-xl border-border"><SelectValue /></SelectTrigger>
                          <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="font-bold">Purpose</Label>
                          <Select defaultValue="SELF_USE" onValueChange={(v) => setValue("purpose", v)}>
                            <SelectTrigger className="rounded-xl border-border"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="SELF_USE">Self Use</SelectItem>
                              <SelectItem value="INVESTMENT">Investment</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="font-bold">Timeframe</Label>
                          <Select defaultValue="IMMEDIATE" onValueChange={(v) => setValue("timeframe", v)}>
                            <SelectTrigger className="rounded-xl border-border"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="IMMEDIATE">Immediate</SelectItem>
                              <SelectItem value="WITHIN_3_MONTHS">Within 3 Months</SelectItem>
                              <SelectItem value="FLEXIBLE">Flexible</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* STEP 3: Size, Budget & Notes */}
                  {step === 3 && (
                    <motion.div
                      key="step-3"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.2 }}
                      className="space-y-6"
                    >
                      <div>
                        <h2 className="text-xl font-bold font-display text-primary">Budget & Size Details</h2>
                        <p className="text-xs text-muted-foreground">Adjust sliders to define your size requirements and budget limits.</p>
                      </div>

                      {/* Dual Budget Slider */}
                      <div className="space-y-3 bg-secondary/30 p-4 border border-border/80 rounded-2xl">
                        <div className="flex justify-between items-center text-xs">
                          <Label className="font-bold flex items-center gap-1.5"><IndianRupee className="w-3.5 h-3.5 text-muted-foreground" /> Budget Range</Label>
                          <span className="font-mono text-primary font-bold bg-primary/10 px-2.5 py-0.5 rounded-full border border-primary/20">
                            {formatCurrency(budgetRange[0])} - {formatCurrency(budgetRange[1])}
                          </span>
                        </div>
                        <Slider
                          min={1000000} // 10L
                          max={200000000} // 20Cr
                          step={500000} // 5L
                          value={budgetRange}
                          onValueChange={(val) => setBudgetRange(val)}
                          className="py-2"
                        />
                      </div>

                      {/* Dual Area Slider */}
                      <div className="space-y-3 bg-secondary/30 p-4 border border-border/80 rounded-2xl">
                        <div className="flex justify-between items-center text-xs">
                          <Label className="font-bold flex items-center gap-1.5"><Ruler className="w-3.5 h-3.5 text-muted-foreground" /> Area Range ({areaUnit})</Label>
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-primary font-bold bg-primary/10 px-2.5 py-0.5 rounded-full border border-primary/20">
                              {areaRange[0]} - {areaRange[1]} {areaUnit}
                            </span>
                            <Select value={areaUnit} onValueChange={(v) => setValue("area_unit", v)}>
                              <SelectTrigger className="h-6 w-20 py-0 text-[10px] rounded-lg border-border"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="sqft">Sq. Ft.</SelectItem>
                                <SelectItem value="sqyd">Sq. Yd.</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <Slider
                          min={50}
                          max={10000}
                          step={50}
                          value={areaRange}
                          onValueChange={(val) => setAreaRange(val)}
                          className="py-2"
                        />
                      </div>

                      {/* Facing Preference & Road Width */}
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="font-bold flex items-center gap-1.5"><Compass className="w-3.5 h-3.5 text-muted-foreground" /> Facing Preference</Label>
                          <Select onValueChange={(v) => setValue("facing_preference", v)}>
                            <SelectTrigger className="rounded-xl border-border"><SelectValue placeholder="Select facing" /></SelectTrigger>
                            <SelectContent>
                              {FACING_OPTIONS.map(opt => <SelectItem key={opt} value={opt.toUpperCase().replace("-", "_")}>{opt}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="font-bold flex items-center gap-1.5"><Landmark className="w-3.5 h-3.5 text-muted-foreground" /> Preferred Road Width</Label>
                          <Select onValueChange={(v) => setValue("preferred_road_width", v)}>
                            <SelectTrigger className="rounded-xl border-border"><SelectValue placeholder="Select width" /></SelectTrigger>
                            <SelectContent>
                              {ROAD_WIDTH_OPTIONS.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="font-bold">Additional Notes</Label>
                        <Textarea rows={2} {...register("notes")} placeholder="Any other specific requirements, local preferences, corner plots etc." data-testid="plot-notes" className="rounded-xl border-border resize-none" />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Action Buttons */}
                <div className="flex justify-between items-center gap-3 pt-4 border-t border-border/60">
                  {step > 1 ? (
                    <Button key="btn-back" type="button" variant="outline" onClick={prevStep} className="font-bold gap-1.5 rounded-xl px-5 py-2.5">
                      <ChevronLeft className="w-4 h-4" /> Back
                    </Button>
                  ) : (
                    <div />
                  )}

                  {step < 3 ? (
                    <Button key="btn-next" type="button" onClick={nextStep} className="font-bold gap-1.5 rounded-xl px-5 py-2.5 ml-auto">
                      Next <ChevronRight className="w-4 h-4" />
                    </Button>
                  ) : (
                    <Button key="btn-submit" type="submit" disabled={isSubmitting} className="font-bold gap-1.5 rounded-xl px-6 py-2.5 ml-auto bg-primary text-white shadow-md hover:opacity-90">
                      {isSubmitting ? "Submitting…" : "Submit Request"} <Sparkles className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Matches Display Section */}
      {matches?.matches?.length > 0 && (
        <section className="bg-secondary/40 py-16 border-t border-border">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-5 h-5 text-primary shrink-0" />
              <h2 className="font-display text-3xl font-bold text-primary">Published matches found</h2>
            </div>
            <p className="text-muted-foreground mb-8">We found these published plot listings matching your criteria.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {matches.matches.map(p => <PropertyCard key={p.property_id} property={p}/>)}
            </div>
          </div>
        </section>
      )}
      {/* The shared dialog traps focus, supports Escape, and restores focus on close. */}
      <Dialog open={showSuccessOverlay} onOpenChange={handleSuccessOpenChange}>
        <DialogContent className="max-w-sm rounded-3xl p-8 text-center" data-testid="plot-demand-success-dialog">
          <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-500 flex items-center justify-center">
            <CheckCircle2 className="w-9 h-9 stroke-[2.5px]" aria-hidden="true" />
          </div>
          <DialogHeader className="text-center sm:text-center">
            <DialogTitle className="font-display text-2xl font-black tracking-tight text-primary">Request submitted</DialogTitle>
            <DialogDescription className="leading-relaxed">
              Your Plots on Demand request has been registered. You will be redirected to your requests shortly.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-center">
            <Button type="button" onClick={() => navigate("/dashboard/plot-demands")} className="w-full sm:w-auto">
              View my requests
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
